<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />

        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions">
          <div class="mb-2">
            <div class="dropdown">
              <a
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    @click.prevent="exportCsv"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as CSV
                  </a>
                </li>
                <li>
                  <a
                    class="dropdown-item rounded-1"
                    @click.prevent="exportPdf"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row">
        <!-- KPI -->
        <div class="col-xl-6 d-flex">
          <div class="row flex-fill dq-stat-grid">
            <div v-for="c in kpiCards" :key="c.title" class="col-md-6 d-flex">
              <div class="card flex-fill dq-stat-card">
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1">
                        {{ c.title }}
                      </span>
                      <h5 v-if="c.type === 'money'">
                        {{ currency }}
                        {{
                          Number(c.value).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                          })
                        }}
                      </h5>
                      <h5 v-else>{{ c.value }}</h5>
                    </div>

                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md avatar-rounded"
                      :class="`bg-transparent-${c.cls} border border-${c.cls}`"
                    >
                      <span :class="`text-${c.cls}`">
                        <i class="ti ti-percentage"></i>
                      </span>
                    </a>
                  </div>

                  <p class="fs-12 fw-normal d-flex align-items-center text-truncate mb-0">
                    <span class="text-muted">Based on selected period</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Filter -->
        <div class="col-xl-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0 pb-0">
              <div class="d-flex flex-wrap justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <span class="me-2">
                    <i class="ti ti-calendar-stats text-primary"></i>
                  </span>
                  <h5>Filter Period</h5>
                </div>

                <div class="input-icon-end position-relative" style="min-width: 230px">
                  <input
                    type="text"
                    class="form-control date-range bookingrange"
                    ref="dateRangeInput"
                    placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  />
                  <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
                </div>
              </div>
            </div>

            <div class="card-body py-3">
              <p class="mb-0 text-muted">
                Showing interest earned between
                <strong>{{ filters.from }}</strong> and <strong>{{ filters.to }}</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <h5>Interest Earned Report</h5>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <InterestEarnedReportTable
              :rows="store.interestEarnedRows"
              :loading="store.interestEarnedLoading"
              :currency="currency"
              :total="store.interestEarnedTotal"
              :page-size="store.interestEarnedPageSize"
              :current="store.interestEarnedCurrent"
              @change="onTableChange"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a></p>
    </div>
  </div>
</template>

<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import DateRangePicker from "daterangepicker";
import moment from "moment";
import { ref, reactive, onMounted, computed } from "vue";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";
import InterestEarnedReportTable from "./interest-earned-report-table.vue";

const title = "Interest Earned Report";
const text = "HR";
const text1 = "Loans & Savings";

const store = useEmployeeSalaryStore();
const dateRangeInput = ref(null);

const filters = reactive({
  from: moment().startOf("year").format("YYYY-MM"),
  to: moment().format("YYYY-MM"),
  page: 1,
  per_page: 25,
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function initRangePicker() {
  if (!dateRangeInput.value) return;

  const start = moment(filters.from, "YYYY-MM").startOf("month");
  const end = moment(filters.to, "YYYY-MM").endOf("month");

  const onPick = (s, e) => {
    filters.from = moment(s).format("YYYY-MM");
    filters.to = moment(e).format("YYYY-MM");
    filters.page = 1;
    fetchReport();
  };

  new DateRangePicker(
    dateRangeInput.value,
    {
      startDate: start,
      endDate: end,
      showDropdowns: true,
      autoApply: true,
      ranges: {
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Year to Date": [moment().startOf("year"), moment().endOf("month")],
        "Last Year": [
          moment().subtract(1, "year").startOf("year"),
          moment().subtract(1, "year").endOf("year"),
        ],
      },
    },
    onPick
  );
}

async function fetchReport() {
  await store.fetchInterestEarnedReport({
    from: filters.from,
    to: filters.to,
    page: filters.page,
    per_page: filters.per_page,
  });
}

function onTableChange(pagination) {
  filters.page = pagination.current;
  filters.per_page = pagination.pageSize;
  fetchReport();
}

function exportCsv() {
  store.exportInterestEarnedReport("csv", {
    from: filters.from,
    to: filters.to,
  });
}

function exportPdf() {
  store.exportInterestEarnedReport("pdf", {
    from: filters.from,
    to: filters.to,
  });
}

const summary = computed(() => store.interestEarnedSummary || {});
const currency = computed(() => summary.value.currency || "KES");

const kpiCards = computed(() => [
  {
    title: "Total Interest",
    value: summary.value.total_interest || 0,
    cls: "success",
    type: "money",
  },
  {
    title: "Loans Count",
    value: summary.value.loans_count || 0,
    cls: "primary",
    type: "count",
  },
  {
    title: "Employees",
    value: summary.value.employee_count || 0,
    cls: "warning",
    type: "count",
  },
  {
    title: "Months in Report",
    value: summary.value.months_count || 0,
    cls: "info",
    type: "count",
  },
]);

onMounted(() => {
  initRangePicker();
  fetchReport();
});
</script>

<style scoped>
@media (max-width: 767.98px) {
  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .card-header .input-icon-end {
    width: 100%;
    min-width: 0 !important;
    margin-top: 0.75rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


